import React from "react";
import { Button } from "../../ui";
import { AssetTableItem, CampaignAsset } from "../../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faGripVertical,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import AssetActions from "./AssetActions.tsx";
import AssetAccessList from "./AssetAccessList.tsx";
import CustomAssetReferenceEditor from "@/components/rich-text-editor/CustomAssetReferenceEditor.tsx";
import { Tooltip } from "../../ui";

export interface AssetCardProps {
  /** The asset data to display */
  asset: AssetTableItem | CampaignAsset;
  /** Asset display variant */
  variant?: "default" | "campaign" | "tree";
  /** Whether the asset is expanded (for tree view) */
  isExpanded?: boolean;
  /** Whether the asset has children (for tree view) */
  hasChildren?: boolean;
  /** Nesting level for tree display */
  level?: number;
  /** Whether to show actions */
  showActions?: boolean;
  /** Whether to show access list */
  showAccessList?: boolean;
  /** Custom actions for the asset */
  actions?: Array<{
    label: string;
    icon?: IconDefinition;
    onClick: () => void;
    variant?: "primary" | "secondary" | "danger" | "ghost";
    className?: string;
  }>;
  /** Callback when expand/collapse is triggered */
  onToggleExpand?: () => void;
  /** Callback when asset is clicked */
  onClick?: () => void;
  /** Whether the asset can be dragged */
  isDraggable?: boolean;
  /** Whether to hide the expand/collapse button (e.g., when used in TreeView) */
  hideExpandButton?: boolean;
  /** Additional CSS classes */
  className?: string;
  hideDescription?: boolean;
}

const AssetCard: React.FC<AssetCardProps> = ({
  asset,
  variant = "default",
  isExpanded = false,
  hasChildren = false,
  level = 0,
  showActions = true,
  showAccessList = false,
  actions = [],
  onToggleExpand,
  onClick,
  isDraggable = false,
  hideExpandButton = false,
  className = "",
  hideDescription = false,
}) => {
  // Extract asset data based on type
  const assetData: AssetTableItem =
    "asset" in asset ? (asset as CampaignAsset).asset : (asset as AssetTableItem);
  const campaignAssetData: CampaignAsset | null =
    "asset" in asset ? (asset as CampaignAsset) : null;

  // Determine card styling based on variant and level
  const getCardClasses = () => {
    const baseClasses =
      "bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-all duration-200";

    if (variant === "tree" && level > 0) {
      return `${baseClasses} border-l-4 border-l-blue-500`;
    }

    return baseClasses;
  };

  const getCustomStyle = () => {
    if (variant === "tree" && level > 0) {
      return { marginLeft: `${level}rem` };
    }
    return {};
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  return (
    <div className={`${getCardClasses()} ${className}`} style={getCustomStyle()}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center flex-1 min-w-0" onClick={handleCardClick}>
          {/* Expand/Collapse Button for Tree View */}
          {variant === "tree" && hasChildren && !hideExpandButton && (
            <Button
              onClick={handleExpandClick}
              variant="ghost"
              size="sm"
              className="text-yellow-400 hover:text-yellow-300 p-1 mr-2 flex-shrink-0"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} size="xs" />
            </Button>
          )}

          {/* Tree Level Indicator */}
          {variant === "tree" && level > 0 && (
            <span className="text-gray-500 text-sm mr-2 flex-shrink-0">└─</span>
          )}

          {/* Drag Indicator */}
          {isDraggable && (
            <FontAwesomeIcon
              icon={faGripVertical}
              className="text-gray-400 mr-3 flex-shrink-0"
              title="Drag to add to campaign"
            />
          )}

          {/* Asset Name and Badges */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4
                className={`font-medium text-white truncate ${onClick ? "cursor-pointer hover:text-yellow-400" : ""}`}
                title={assetData.name}
              >
                {assetData.name}
              </h4>

              {/* Level Badge */}
              {variant === "tree" && level > 0 && (
                <span className="bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs flex-shrink-0">
                  Child Asset
                </span>
              )}

              {/* Children Count Badge */}
              {hasChildren && (
                <span className="bg-blue-600 text-blue-200 px-2 py-1 rounded text-xs flex-shrink-0">
                  {assetData.children?.length || 0} children
                </span>
              )}

              {/* Marketplace Icon */}
              {assetData.isFromMarketplace && (
                <Tooltip content="Acquired from Marketplace">
                  <span className="text-amber-500 flex-shrink-0 ml-1">
                    <FontAwesomeIcon icon={faStore} size="xs" />
                  </span>
                </Tooltip>
              )}
            </div>

            {/* Asset Metadata */}
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <span>{assetData.type}</span>
              <span>•</span>
              <span>{assetData.category}</span>
              {assetData.world && (
                <>
                  <span>•</span>
                  <span>{assetData.world}</span>
                </>
              )}
            </div>

            {/* Description */}
            {!hideDescription && assetData.description && (
              <p className="text-gray-300 text-sm line-clamp-2" title={assetData.description}>
                <CustomAssetReferenceEditor readOnly value={assetData.description} />
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            <AssetActions asset={asset} actions={actions} variant={variant} />
          </div>
        )}
      </div>

      {/* Access List */}
      {showAccessList && campaignAssetData?.accessUsers && (
        <AssetAccessList
          accessUsers={campaignAssetData.accessUsers}
          onRevokeAccess={(characterId: number) => {
            // This would be handled by parent component
            console.log("Revoke access for character:", characterId);
          }}
        />
      )}
    </div>
  );
};

export default AssetCard;
